/**
 * Darstellung der vier Zustände der automatischen Akkorderkennung – **eine** Definition für alle
 * Anzeigeorte (Panel-Badge, Tab-Punkt, Vorschau-Chip, Verlauf).
 *
 * Bewusst eine reine, modul-weite Tabelle statt objekt-liefernder Computeds: die Konsumenten
 * schlagen per Zustand nach und behalten damit stabile Referenzen (vgl. das `oldValue`-Pattern in
 * `webapp/src/VUE_RULES.md`).
 */
import {INFERRED_CHORD_COLOR, type ChordInferenceState} from '@/composables/musicXmlDom';

export interface ChordInferenceDisplay {
    /** Ausführliches Label (Panel, Detail-Dialog). `{n}` wird durch die Anzahl ersetzt. */
    label: string;
    /** Kurzes Label für enge Kontexte (Vorschau-Toolbar, Verlaufs-Eintrag). */
    shortLabel: string;
    /** Vollständiger Satz als `title`-Tooltip. */
    title: string;
    /** Tailwind-Klassen für den Badge-Körper. */
    classes: string;
    /** Iconify-Name; `null` → stattdessen der farbige Punkt aus `dotColor`. */
    icon: string | null;
    /** Punktfarbe, wenn kein Icon gesetzt ist. */
    dotColor: string | null;
    /**
     * Farbe des Hinweis-Punkts am Tab „Akkorderkennung"; `null` = kein Punkt. Die ruhigen Zustände
     * (`never`/`removed`) erzeugen bewusst keinen Hinweis – es gibt nichts zu beachten.
     */
    tabDotColor: string | null;
}

export const CHORD_INFERENCE_DISPLAY: Record<ChordInferenceState, ChordInferenceDisplay> = {
    active: {
        label: 'Auto-Akkorde: {n}',
        shortLabel: 'Auto-Akkorde: {n}',
        title: 'Automatische Akkorderkennung angewendet – {n} erkannte Akkordsymbole',
        classes: 'bg-purple-100 text-purple-700 border-purple-200',
        icon: null,
        dotColor: INFERRED_CHORD_COLOR,
        tabDotColor: INFERRED_CHORD_COLOR,
    },
    original: {
        label: 'Datei hat bereits Akkorde',
        shortLabel: 'Hat Akkorde',
        title: 'Diese Datei enthält bereits eigene Akkordsymbole (nicht automatisch erkannt).',
        classes: 'bg-sky-50 text-sky-700 border-sky-200',
        icon: 'game-icons:guitar-head',
        dotColor: null,
        tabDotColor: '#0284c7',
    },
    empty: {
        label: 'Analysiert – keine gefunden',
        shortLabel: 'Keine gefunden',
        title:
            'Die automatische Akkorderkennung ist gelaufen, hat aber keine Akkorde gesetzt. ' +
            'Mögliche Gründe: die Empfindlichkeit ist zu hoch, oder alle Takte hatten bereits Akkordsymbole.',
        classes: 'bg-amber-50 text-amber-800 border-amber-200',
        icon: 'fa6-solid:circle-info',
        dotColor: null,
        tabDotColor: '#d97706',
    },
    removed: {
        label: 'Auto-Akkorde entfernt',
        shortLabel: 'Entfernt',
        title: 'Automatisch erkannte Akkorde wurden wieder entfernt',
        classes: 'bg-gray-100 text-gray-500 border-gray-200',
        icon: 'fa6-solid:eraser',
        dotColor: null,
        tabDotColor: null,
    },
    never: {
        label: 'Akkorde noch nicht analysiert',
        shortLabel: 'Akkordanalyse fehlt',
        title: 'Für diese Datei lief noch keine automatische Akkorderkennung',
        classes: 'bg-gray-100 text-gray-500 border-gray-200',
        icon: null,
        dotColor: '#d1d5db',
        tabDotColor: null,
    },
};

/** Ersetzt den `{n}`-Platzhalter durch die Anzahl erkannter Akkordsymbole. */
export function formatChordInferenceText(text: string, count: number): string {
    return text.replace('{n}', String(count));
}
